import React, { useEffect, useState } from 'react'
import ToastMessage from '../../../../common/ToastMessage';
import CustomDataGrid from '../../../../common/CustomDataGrid';
import useInterventionTypes from '../../../../store/ReactQueries/interventionTypes/useInterventionType';
import useAddInterventionType from '../../../../store/ReactQueries/interventionTypes/useAddInterventionTypes';
import useUpdateInterventionType from '../../../../store/ReactQueries/interventionTypes/useUpdateInterventionType';
import useDeleteInterventionType from '../../../../store/ReactQueries/interventionTypes/useDeleteInterventionTypes';
import { GridColDef } from '@mui/x-data-grid';
import { PAGESIZE } from '../../../../utils/constants';
import { FieldErrors } from '../../../../domain/helpers/Errors';
import { formatDate } from '../../../../common/GlobalFunctions';
import { validateSchema } from '../../../../common/ValidateSchema';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { InterventionTypeInterface } from '../../../../models/TicketModule/InterventionType';
import { interventionTypeSchema } from '../../../../domain/ValidationSchemas/Ticket/interventionType';

export const InterventionType = () => {
    const { language } = useLanguageStore();
    const { darkMode } = useDarkModeStore();

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(PAGESIZE);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [form, setForm] = useState<InterventionTypeInterface>({
        id: '',
        title: '',
        isDefault: false
    });

    const [modal, setModal] = useState<{ add: boolean, edit: boolean, view: boolean }>({
        add: false,
        edit: false,
        view: false
    });

    let { data: interventionTypes, error: interventionTypesError, isLoading, refetch } = useInterventionTypes(page, pageSize);

    const addIntervenantionType = useAddInterventionType(page, pageSize);
    const updateIntervenationType = useUpdateInterventionType(page, pageSize);
    const deleteIntervenationType = useDeleteInterventionType(page, pageSize);

    useEffect(() => {
        if (interventionTypesError) ToastMessage.Failed(`Error: ${interventionTypesError.message}`);
    }, [interventionTypesError]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Id', type: "string", editable: false, width: 150 },
        { field: 'title', headerName: 'Title', type: "string", editable: true, width: 200 },
        {
            field: 'isDefault',
            headerName: 'IsDefault',
            type: 'boolean',
            editable: true,
            renderCell: ({ value }) => (
                <span style={{ color: darkMode ? 'white' : 'black' }}>
                    {value ? `✓` : '✕'}
                </span>
            ),
        },
        { field: 'createdDate', headerName: 'CreatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'updatedDate', headerName: 'UpdatedDate', type: "dateTime", editable: false, width: 150, valueFormatter: formatDate },
        { field: 'tenantId', headerName: 'TenantId', type: "string", editable: false, width: 150 },
        { field: 'authorId', headerName: 'AuthorId', type: "string", editable: false, width: 150 },
        { field: 'modifiedById', headerName: 'ModifiedById', type: "string", editable: false, width: 150 },
    ];

    const addItems = () => {
        setForm({ ...form, title: '', id: '', isDefault: false });
        setModal({ ...modal, add: true });
        setErrors({});
    }

    const handleError = (error: unknown) => {
        if (error instanceof Error) {
            console.log(error.message);
            ToastMessage.Failed(error.message);
        } else {
            console.log("An unknown error occurred");
            ToastMessage.Failed();
        }
    };

    const handleSave = async () => {
        const isValid = validateSchema(interventionTypeSchema, { title: form.title });

        setErrors(isValid.errors);
        if (!isValid.result) return;

        const upForm: InterventionTypeInterface = {
            id: form.id,
            title: form.title,
            isDefault: form.isDefault
        }

        if (upForm.id === '') {
            const { id, ...formData } = upForm;
            addIntervenantionType.mutate(formData, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success()
                },
                onError: (error: unknown) => handleError(error),
            });
        } else {
            updateIntervenationType.mutate(upForm, {
                onSuccess: () => {
                    if (upForm.isDefault) refetch();
                    ToastMessage.Success();
                },
                onError: (error: unknown) => handleError(error),
            });
        }
        setModal({ ...modal, add: false, edit: false, view: false });
    }

    const handleDelete = async (id: string) => {
        if (await ToastMessage.Confirm(language['ToastDeleteItem'])) {
            deleteIntervenationType.mutate(id, {
                onSuccess: () => ToastMessage.Success(),
                onError: (error: unknown) => handleError(error),
            })
        }
    };

    return (
        <CustomDataGrid
            name={language['InterventionType']}
            rows={interventionTypes || []}
            showColumnModal={['title', 'isDefault']}
            hideColumns={['authorId', 'id', 'tenantId', 'modifiedById']}
            form={form}
            page={page}
            modal={modal}
            errors={errors}
            columns={columns}
            loading={isLoading}
            pageSize={pageSize}
            setForm={setForm}
            setPage={setPage}
            setModal={setModal}
            addItems={addItems}
            setErrors={setErrors}
            handleSave={handleSave}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
        />
    );
}
export default InterventionType;